import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Users,
  Loader2,
} from "lucide-react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
  parseISO,
} from "date-fns";
import { es } from "date-fns/locale";
import api from "@/lib/api-client";
import { useLocation } from "@/contexts/LocationContext";

interface ReservationSummary {
  date: string;
  total: number;
  byShift: {
    lunch: number;
    dinner: number;
  };
  totalPax: number;
  byShiftPax: {
    lunch: number;
    dinner: number;
  };
}

interface ScheduleConfig {
  shifts: {
    name: string;
    startTime: string;
    endTime: string;
    enabled: boolean;
  }[];
}

export default function ReservationsCalendar() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentLocationId } = useLocation();

  // Initialize month from URL or current date
  const [currentMonth, setCurrentMonth] = useState(() => {
    const monthParam = searchParams.get("month");
    if (monthParam) {
      const parsed = parseISO(`${monthParam}-01`);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    return new Date();
  });

  const [reservationData, setReservationData] = useState<
    Record<string, ReservationSummary>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig | null>(
    null
  );

  // Load schedule config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await api.get("/schedule/config");
        if (response.data.success && response.data.config) {
          setScheduleConfig(response.data.config);
        }
      } catch (error) {
        console.error("Error loading schedule config:", error);
      }
    };
    loadConfig();
  }, []);

  // Get shift names from config
  const shiftNames = useMemo(() => {
    if (!scheduleConfig?.shifts) {
      return { lunch: "Comida", dinner: "Cena" };
    }
    let shifts = scheduleConfig.shifts;
    if (typeof shifts === "string") {
      shifts = JSON.parse(shifts);
    }
    const enabledShifts = shifts.filter((s: { enabled: boolean }) => s.enabled);
    return {
      lunch: enabledShifts[0]?.name || "Comida",
      dinner: enabledShifts[1]?.name || "Cena",
    };
  }, [scheduleConfig]);

  // Determine lunch/dinner cutoff time (default: 17:00)
  const lunchCutoffHour = useMemo(() => {
    if (!scheduleConfig?.shifts) return 17;
    let shifts = scheduleConfig.shifts;
    if (typeof shifts === "string") {
      shifts = JSON.parse(shifts);
    }
    const lunchShift = shifts.find(
      (s: { name: string; enabled: boolean }) =>
        s.enabled && s.name.toLowerCase().includes("comida")
    );
    if (lunchShift?.endTime) {
      const [h] = lunchShift.endTime.split(":").map(Number);
      return h;
    }
    return 17;
  }, [scheduleConfig]);

  // Load reservation data for the month
  useEffect(() => {
    const loadMonthData = async () => {
      if (!currentLocationId) return;

      setIsLoading(true);
      try {
        const start = format(startOfMonth(currentMonth), "yyyy-MM-dd");
        const end = format(endOfMonth(currentMonth), "yyyy-MM-dd");

        // Fetch all reservations for the month
        const response = await api.get(
          `/reservations?locationId=${currentLocationId}&startDate=${start}&endDate=${end}&limit=1000`
        );

        const reservations = response.data.data || [];

        // Group by date
        const grouped: Record<string, ReservationSummary> = {};

        reservations.forEach(
          (res: { date: string; time: string; partySize: number }) => {
            const dateKey = res.date.split("T")[0];
            if (!grouped[dateKey]) {
              grouped[dateKey] = {
                date: dateKey,
                total: 0,
                byShift: { lunch: 0, dinner: 0 },
                totalPax: 0,
                byShiftPax: { lunch: 0, dinner: 0 },
              };
            }

            grouped[dateKey].total += 1;
            grouped[dateKey].totalPax += res.partySize;

            // Determine shift based on time
            const hour = parseInt(res.time.split(":")[0], 10);
            if (hour < lunchCutoffHour) {
              grouped[dateKey].byShift.lunch += 1;
              grouped[dateKey].byShiftPax.lunch += res.partySize;
            } else {
              grouped[dateKey].byShift.dinner += 1;
              grouped[dateKey].byShiftPax.dinner += res.partySize;
            }
          }
        );

        setReservationData(grouped);
      } catch (error) {
        console.error("Error loading month data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMonthData();
  }, [currentMonth, currentLocationId, lunchCutoffHour]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth(
      direction === "prev"
        ? subMonths(currentMonth, 1)
        : addMonths(currentMonth, 1)
    );
  };

  const goToDay = (date: Date) => {
    navigate(`/reservations?date=${format(date, "yyyy-MM-dd")}`);
  };

  // Calculate monthly totals
  const monthlyTotals = useMemo(() => {
    let total = 0;
    let lunch = 0;
    let dinner = 0;
    let totalPax = 0;
    let lunchPax = 0;
    let dinnerPax = 0;

    Object.values(reservationData).forEach((day) => {
      total += day.total;
      lunch += day.byShift.lunch;
      dinner += day.byShift.dinner;
      totalPax += day.totalPax;
      lunchPax += day.byShiftPax.lunch;
      dinnerPax += day.byShiftPax.dinner;
    });

    return { total, lunch, dinner, totalPax, lunchPax, dinnerPax };
  }, [reservationData]);

  // Totales del mes: en escritorio van en la misma línea que el título (así la
  // cabecera ocupa menos alto y el mes cabe entero sin scroll); en móvil, debajo.
  const totalsBar = (className: string) => (
    <div className={`flex items-center gap-4 md:gap-6 text-xs md:text-sm ${className}`}>
      <div className="flex items-center gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <span className="text-gray-500 hidden sm:inline">{shiftNames.lunch}:</span>
        <span className="font-semibold text-amber-600">{monthlyTotals.lunch}</span>
        <span className="text-gray-400">({monthlyTotals.lunchPax}p)</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-indigo-400" />
        <span className="text-gray-500 hidden sm:inline">{shiftNames.dinner}:</span>
        <span className="font-semibold text-indigo-600">{monthlyTotals.dinner}</span>
        <span className="text-gray-400">({monthlyTotals.dinnerPax}p)</span>
      </div>
      <div className="flex items-center gap-1.5 pl-3 border-l border-gray-200">
        <span className="text-gray-500 hidden sm:inline">Total:</span>
        <span className="font-bold text-gray-900">{monthlyTotals.total}</span>
        <span className="text-gray-400">({monthlyTotals.totalPax}p)</span>
      </div>
    </div>
  );

  return (
    // min-h-full (no h-full + overflow-hidden): la rejilla tiene altura mínima
    // por fila y, si no cabe en la pantalla, la página hace scroll en vez de
    // recortar las últimas filas o el pie de cada celda.
    <div className="min-h-full flex flex-col bg-gray-50">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2.5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/reservations")}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-gray-900">
                Vista Mensual de Reservas
              </h1>
              <p className="text-gray-500 text-xs hidden sm:block">
                Resumen de reservas por día y turno
              </p>
            </div>
          </div>

          {totalsBar("hidden md:flex")}

          {/* Month navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateMonth("prev")}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2 min-w-[140px] sm:min-w-[180px] justify-center">
              <Calendar className="w-4 h-4 text-primary-600 hidden sm:block" />
              <span className="text-sm sm:text-base font-semibold text-gray-900 capitalize">
                {format(currentMonth, "MMMM yyyy", { locale: es })}
              </span>
            </div>
            <button
              onClick={() => navigateMonth("next")}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => setCurrentMonth(new Date())}
              className="ml-1 px-2.5 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md"
            >
              Hoy
            </button>
          </div>
        </div>

        {totalsBar("md:hidden justify-center mt-2")}
      </div>

      {/* Calendar Grid - Fills remaining space */}
      <div className="flex-1 flex flex-col p-2 md:p-4">
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
          {/* Weekday headers */}
          <div className="flex-shrink-0 grid grid-cols-7 bg-gray-50 border-b border-gray-200">
            {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((day) => (
              <div
                key={day}
                className="py-2 text-center text-xs md:text-sm font-medium text-gray-500"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days - Fills remaining space */}
          {isLoading ? (
            <div className="flex-1 min-h-[24rem] flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : (
            <div className="flex-1 grid grid-cols-7 grid-rows-[repeat(6,minmax(5rem,1fr))] md:grid-rows-[repeat(6,minmax(5.5rem,1fr))]">
              {calendarDays.map((day, index) => {
                const dateKey = format(day, "yyyy-MM-dd");
                const data = reservationData[dateKey];
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isTodayDate = isToday(day);

                return (
                  <button
                    key={index}
                    onClick={() => goToDay(day)}
                    className={`
                      relative p-1.5 md:p-2 border-b border-r border-gray-100 text-left transition-colors hover:bg-gray-50 overflow-hidden
                      ${!isCurrentMonth ? "bg-gray-50/50" : ""}
                      ${isTodayDate ? "ring-2 ring-inset ring-primary-500" : ""}
                    `}
                  >
                    {/* Day number */}
                    <div
                      className={`
                        text-xs md:text-sm font-medium
                        ${!isCurrentMonth ? "text-gray-300" : "text-gray-700"}
                        ${isTodayDate ? "text-primary-600 font-bold" : ""}
                      `}
                    >
                      {format(day, "d")}
                      {isTodayDate && (
                        <span className="ml-0.5 text-[10px] font-normal hidden md:inline">
                          (Hoy)
                        </span>
                      )}
                    </div>

                    {/* Reservation data: nombre del turno + nº de reservas
                        (discreto) y, a la derecha, las PERSONAS en negrita —
                        el dato que el hostelero quiere ver de un vistazo. */}
                    {isCurrentMonth && data && data.total > 0 && (
                      <div className="mt-0.5 space-y-0.5">
                        {/* Lunch */}
                        {data.byShift.lunch > 0 && (
                          <div className="flex items-center gap-1 text-[10px] md:text-xs leading-tight">
                            <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-amber-400 flex-shrink-0" />
                            <span className="text-amber-700 font-medium hidden lg:inline truncate">
                              {shiftNames.lunch}
                            </span>
                            <span className="text-gray-500">
                              {data.byShift.lunch}
                              <span className="hidden lg:inline"> res.</span>
                            </span>
                            <span className="ml-auto font-bold text-gray-900 hidden md:inline whitespace-nowrap">
                              {data.byShiftPax.lunch}
                              <span className="font-normal text-gray-400"> pers.</span>
                            </span>
                          </div>
                        )}

                        {/* Dinner */}
                        {data.byShift.dinner > 0 && (
                          <div className="flex items-center gap-1 text-[10px] md:text-xs leading-tight">
                            <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-indigo-400 flex-shrink-0" />
                            <span className="text-indigo-700 font-medium hidden lg:inline truncate">
                              {shiftNames.dinner}
                            </span>
                            <span className="text-gray-500">
                              {data.byShift.dinner}
                              <span className="hidden lg:inline"> res.</span>
                            </span>
                            <span className="ml-auto font-bold text-gray-900 hidden md:inline whitespace-nowrap">
                              {data.byShiftPax.dinner}
                              <span className="font-normal text-gray-400"> pers.</span>
                            </span>
                          </div>
                        )}

                        {/* Total */}
                        <div className="flex items-center gap-1 text-[10px] md:text-xs leading-tight pt-0.5 border-t border-gray-100">
                          <Users className="w-2.5 h-2.5 md:w-3 md:h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-500 hidden lg:inline">Total</span>
                          <span className="text-gray-500">
                            {data.total}
                            <span className="hidden lg:inline"> res.</span>
                          </span>
                          <span className="ml-auto font-bold text-gray-900 hidden md:inline whitespace-nowrap">
                            {data.totalPax}
                            <span className="font-normal text-gray-400"> pers.</span>
                          </span>
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
